// Payment Posting v2 — data layer. Pure helpers over the eobs-v2 payloads.
// Rule: render API values verbatim. Never fabricate, correct, or hide a field the
// API returns. Anything the API does not return is omitted, not invented.

export const sm = (f) => f.summary || f.extraction_summary || {};
export const amtOf = (f) => { const s = sm(f); const v = s.check_amount ?? s.total_paid; return isNaN(Number(v)) ? 0 : Number(v); };
export const payerName = (f) => sm(f).payer_name || "";
export const payeeName = (f) => sm(f).payee_name || f.payee_name || "";
export const checkNo = (f) => sm(f).check_number || "";
export const claimCount = (f) => { const c = sm(f).claim_count; return c == null ? null : c; };
export const checkDate = (f) => sm(f).check_date || null;
export const uploadedBy = (f) => f.uploaded_by_email || f.uploaded_by_name || "";
export const fileName = (f) => f.filename || "";
export const norm = (s) => String(s || "").trim().toLowerCase();

// Duplicate copies are flagged by the API (f.duplicate). We never derive our own.
export const isDupCopy = (f) => { const d = f.duplicate; return !!(d && d.is_duplicate && !d.is_original); };

export const eraStatusOf = (f) => f.era_downloaded_at ? "downloaded" : (f.era_generated_at ? "generated" : "none");
export const procStatusOf = (f) => {
  const s = String(f.status || "").toLowerCase(), j = String(f.job_status || "").toLowerCase();
  if (s === "failed" || s === "error" || j === "failed") return "failed";
  if (s === "queued" || j === "queued") return "queued";
  if (s === "processing" || s === "in_progress" || s === "pending" || j === "processing" || j === "running") return "processing";
  if (f.needs_review) return "needs_review";
  if (s === "processed") return "processed";
  return "processing";
};
export const isProcessed = (f) => ["processed", "needs_review"].includes(procStatusOf(f));

// Date age from the check date (verbatim; today is the client clock).
export const checkAgeDays = (iso) => {
  const d = checkDate({ summary: { check_date: iso } }); // not used
  return null;
};
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
// mm-dd-yyyy, mm/dd/yyyy, yyyy-mm-dd, "August 14, 2018" → { y, m, d } or null.
const parseYMD = (raw) => {
  const s = String(raw || "").trim(); let y, m, d, x;
  if ((x = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/))) [m, d, y] = [+x[1], +x[2], +x[3]];
  else if ((x = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/))) [y, m, d] = [+x[1], +x[2], +x[3]];
  else if ((x = s.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/)) && MONTHS.indexOf(x[1].slice(0, 3).toLowerCase()) >= 0) [m, d, y] = [MONTHS.indexOf(x[1].slice(0, 3).toLowerCase()) + 1, +x[2], +x[3]];
  else return null;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d ? dt : null;
};
const pad = (n) => String(n).padStart(2, "0");
// Display as MM/dd/yyyy; anything we can't read is shown exactly as the API sent it.
export const fmtCheckDate = (raw) => { const dt = parseYMD(raw); return dt ? `${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}/${dt.getFullYear()}` : (raw || ""); };
export const checkDateMs = (raw) => { const dt = parseYMD(raw); if (dt) return dt.getTime(); const t = Date.parse(raw || ""); return isNaN(t) ? null : t; };
export const dateAge = (mmddyyyy) => {
  const t = checkDateMs(mmddyyyy);
  if (t == null) return null;
  return Math.floor((Date.now() - t) / 86400000);
};
export const createdAtMs = (f) => { const t = Date.parse(f.created_at || ""); return isNaN(t) ? null : t; };

export const SINCE = {
  today: () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); },
  d2: () => new Date(Date.now() - 48 * 3.6e6).toISOString(),
  d7: () => new Date(Date.now() - 168 * 3.6e6).toISOString(),
  d30: () => new Date(Date.now() - 720 * 3.6e6).toISOString(),
  all: () => "all",
};
export const SCOPE_LABELS = { today: "Today", d2: "Last 2 days", d7: "Last 7 days", d30: "Last 30 days", all: "All time" };
export const SCOPE_DAYS = { today: 0, d2: 2, d7: 7, d30: 30 };
// Same cut-off the server fetch uses (Today = local midnight, others rolling hours).
export const inScopeOf = (f, scope) => { if (scope === "all") return true; const ms = createdAtMs(f); return ms != null && ms >= Date.parse(SINCE[scope]()); };
export const fmtCreated = (f) => { const ms = createdAtMs(f); if (ms == null) return ""; const d = new Date(ms); return { date: d.toLocaleDateString(), time: d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) }; };
export const STATUS_LABELS = { queued: "Queued", processing: "Processing", processed: "Processed", needs_review: "Needs review", failed: "Failed" };

// readyMap[id] = getEraReadiness(id) response, used verbatim.
export const mapReady = (readyMap, f) => readyMap[f.file_id]?.ready === true;

// Segment bucket (mutually exclusive): downloaded / generated / ready / held / processing.
// Read ERA state off the record we're given (originals only reach here). Unknown
// readiness counts as held until era-readiness resolves. needs_review is a FLAG, not a
// bucket, so it never forces held on its own.
export function bucketOf(f, readyMap) {
  const era = eraStatusOf(f);
  if (era === "downloaded") return "downloaded";
  if (era === "generated") return "generated";
  if (!isProcessed(f)) return "processing";
  return mapReady(readyMap, f) ? "ready" : "held";
}

// Processing-lane predicates (from f.status / f.error, verbatim).
export const isFailed = (f) => procStatusOf(f) === "failed";
export const isInProgress = (f) => ["queued", "processing"].includes(procStatusOf(f));

// Flags (any state) — from the fields the API returns; never derived from money.
export const partiallyComplete = (f) => {
  const s = sm(f);
  const cov = Number(s.coverage_pct);
  return (!isNaN(cov) && cov < 100) || Number(s.missing_claims) > 0 || s.clean_export_blocked === true;
};

// Age-of-check bucket from the check date (client clock is "today").
export const AGE_LABELS = { le30: "≤30d", d3190: "31–90d", d91y: "91d–1y", gt1y: ">1y" };
export const ageBucket = (mmddyyyy) => {
  const d = dateAge(mmddyyyy);
  if (d == null) return null;
  if (d <= 30) return "le30";
  if (d <= 90) return "d3190";
  if (d <= 365) return "d91y";
  return "gt1y";
};

// The account-mapping gaps that hold a check, from its era-readiness (verbatim missing[] + checks[]).
export function heldGates(f, readyMap) {
  const rd = readyMap[f.file_id];
  if (!rd) return null; // unknown until it loads
  const checks = rd.checks || [];
  const missing = rd.missing || [];
  return missing.map((kind) => {
    const chk = checks.find((c) => c.kind === kind) || {};
    return { kind, label: chk.label || kind, entity: chk.entity || "", detected: chk.detected || false, value: chk.value || "" };
  });
}

export const npiValid = (v) => {
  if (!/^\d{10}$/.test(v)) return false;
  const s = "80840" + v; let sum = 0, d2 = false;
  for (let i = s.length - 1; i >= 0; i--) { let d = +s[i]; if (d2) { d *= 2; if (d > 9) d -= 9; } sum += d; d2 = !d2; }
  return sum % 10 === 0;
};
